import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { updateAISettings, initializeUserPreferencesIfNeeded } from '../../../store/actions/SettingsActions';
import { fetchMatterDropdowns } from '../../../store/actions/Matters/MatterActions';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_JURISDICTIONS } from '../../Matters/matterConstants';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { PiGenderFemaleLight, PiGenderMaleLight } from 'react-icons/pi';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, aiSettingsTourSteps } from '../../shared/FeatureTour';

const AISettingsComponent: React.FC<{ style?: CSSProperties; backgroundColor?: string }> = ({
  style,
}) => {
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const aiSettings = userInfo?.preferences?.ai_settings;
  const { t } = useTranslation();
  const [selectedAILanguage, setSelectedAILanguage] = useState<string>('');
  const [selectedVoice, setSelectedVoiceState] = useState<string>('');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('');
  const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);
  const { setIsContentScrollable } = useContext(LayoutContext);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'ai-settings',
    steps: aiSettingsTourSteps,
    autoStartDelay: 600,
  });

  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>(AVAILABLE_JURISDICTIONS);

  const voiceOptions = [
    { label: t('Default'), value: 'default' },
    { label: t('Male'), value: 'male', icon: <PiGenderMaleLight /> },
    { label: t('Female'), value: 'female', icon: <PiGenderFemaleLight /> },
    { label: t('Neutral'), value: 'neutral' },
  ];

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  // Fetch language and jurisdiction options from backend (same as matter creation)
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          if (dropdownsData.languages) {
            setLanguageOptions(dropdownsData.languages);
          }
          // Jurisdictions are static (from matterConstants); no backend fetch needed
        }
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };

    loadDropdowns();
  }, [dispatch]);

  // Ensure preferences are loaded from backend
  useEffect(() => {
    dispatch(initializeUserPreferencesIfNeeded() as any);
  }, [dispatch]);

  // Initialize component with actual values from preferences
  // This ensures the UI always reflects the real backend values, not just fallbacks
  useEffect(() => {
    if (aiSettings && Object.keys(aiSettings).length > 0) {
      // Use actual values from preferences
      setSelectedAILanguage(aiSettings.language || 'en');
      setSelectedVoiceState(aiSettings.speaker_voice || 'default');
      setSelectedJurisdiction(aiSettings.jurisdiction || 'US');
    } else {
      // Set defaults only if no preferences exist yet
      setSelectedAILanguage('en');
      setSelectedVoiceState('default');
      setSelectedJurisdiction('US');
    }
  }, [aiSettings]);

  const handleSelectAILanguage = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedAILanguage) {
      setSelectedAILanguage(value);
      saveAISettings({
        language: value,
        jurisdiction: selectedJurisdiction,
        speaker_voice: selectedVoice,
      });
    }
  };

  const handleSelectAIVoice = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedVoice) {
      setSelectedVoiceState(value);
      saveAISettings({
        language: selectedAILanguage,
        jurisdiction: selectedJurisdiction,
        speaker_voice: value,
      });
    }
  };

  const handleSelectJurisdiction = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedJurisdiction) {
      setSelectedJurisdiction(value);
      saveAISettings({
        language: selectedAILanguage,
        jurisdiction: value,
        speaker_voice: selectedVoice,
      });
    }
  };

  const saveAISettings = async (settings: {
    jurisdiction: string;
    language: string;
    speaker_voice: string;
  }) => {
    dispatch(
      updateAISettings({
        jurisdiction: settings.jurisdiction,
        language: settings.language,
        speaker_voice: settings.speaker_voice,
      }) as any
    );
  };

  return (
    <>
    <div className="space-y-6" style={style}>
      <div className="space-y-2 px-4 pt-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('AI Settings')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Customize AI preferences for a personalized and efficient experience.')}
        </p>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      <div data-tour-id="ai-settings-cards" className="grid grid-cols-2 2xl:grid-cols-3 gap-4 px-4 items-start">
        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="flex flex-col h-full">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                  {t('Jurisdiction')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Select your legal framework.')}
                </p>
              </div>
              <div className="mt-4">
                <Dropdown
                  options={jurisdictionOptions}
                  value={selectedJurisdiction}
                  placeholder={t('Select Jurisdiction')}
                  onChange={handleSelectJurisdiction}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="flex flex-col h-full">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                  {t('Language')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Set the language for AI communication.')}
                </p>
              </div>
              <div className="mt-4">
                <Dropdown
                  options={languageOptions}
                  value={selectedAILanguage}
                  placeholder={t('Select Language')}
                  onChange={handleSelectAILanguage}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="flex flex-col h-full">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                  {t('Speaker Voice')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Choose a voice tailored to your preferences.')}
                </p>
              </div>
              <div className="mt-4">
                <Dropdown
                  options={voiceOptions}
                  value={selectedVoice}
                  placeholder={t('Select Voice')}
                  onChange={handleSelectAIVoice}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FeatureTour
      steps={aiSettingsTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default AISettingsComponent;
